import { useEffect, useRef } from 'react'
import { notifyConversationsChanged } from '@gorilla-works/messages'

import { resolveMessageConversationKey } from '@/lib/message-conversation-key'
import { onTabRefocus } from '@/lib/on-tab-refocus'

/**
 * Keeps the grid's message badges current.
 *
 * Without this, a badge only reflects what was true when the row mounted, plus whatever you did to
 * it yourself. Somebody else's message never appears until a reload — which makes an unread
 * indicator actively misleading rather than merely stale.
 *
 * It does not fetch anything itself: it tells the mounted summary hooks their entities may have
 * moved, and they re-read through the package's batcher. Because that batcher coalesces, a whole
 * page of rows collapses into **one** `summaries` request per tick, not one per row.
 *
 * Mirrors the 5-minute cadence `useStageCountsInit` already uses for tab counts, so the two settle
 * together rather than at unrelated moments.
 */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000

export function useMessageBadgeRefresh(
  entityType: string,
  items: Array<Record<string, any>>,
  enabled: boolean
) {
  // Read through a ref so a re-sorted or re-paged grid does not restart the interval — the tick
  // should stay on its own cadence rather than being reset by every render.
  const itemsRef = useRef(items)
  itemsRef.current = items

  const entityTypeRef = useRef(entityType)
  entityTypeRef.current = entityType

  useEffect(() => {
    if (!enabled) return

    const refresh = () => {
      // Nothing in this microapp pauses polling on tab hide, so every open tab refreshes forever.
      // Skipping while hidden costs nothing — the visibilitychange handler below catches up the
      // moment the tab comes back.
      if (document.visibilityState === 'hidden') return

      const grouped = new Map<string, string[]>()
      itemsRef.current.forEach((item) => {
        const key = resolveMessageConversationKey(entityTypeRef.current, item)
        if (!key) return
        const ids = grouped.get(key.entityTypeId)
        if (ids) ids.push(key.entityId)
        else grouped.set(key.entityTypeId, [key.entityId])
      })

      grouped.forEach((entityIds, entityTypeId) => notifyConversationsChanged(entityTypeId, entityIds))
    }

    const interval = setInterval(refresh, REFRESH_INTERVAL_MS)

    // Coming back to a tab that has been hidden for a while is exactly when the badges are most
    // stale, so refresh on return — but only for a real tab switch, not the sub-second iframe
    // reattach flicker that fires on every mouse move (see onTabRefocus).
    const stopRefocus = onTabRefocus(refresh)

    return () => {
      clearInterval(interval)
      stopRefocus()
    }
  }, [enabled])
}
